import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Menu,
    MenuItem,
    IconButton
} from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import DistributionCenterInventoryHistoryService from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistoryCtrl';
import { DistributionCenterInventoryHistory } from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistory';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { DataGrid, esES, GridColDef } from '@mui/x-data-grid';

interface ListInventoryHistoryProps {
    distributionCenterId: string;
}
const ListInventoryHistory: React.FunctionComponent<ListInventoryHistoryProps> = (props) => {
    const { distributionCenterId } = props;
    const distributionCenterInventoryHistoryService = useMemo(
        () => new DistributionCenterInventoryHistoryService(),
        []
    );
    const [inventoryHistories, setInventoryHistories] = useState<DistributionCenterInventoryHistory[]>([]);
    const [filters, setFilters] = useState({ search: '' });
    const [selectedHistory, setSelectedHistory] = useState<DistributionCenterInventoryHistory | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedHistoryRow, setSelectedHistoryRow] = useState<DistributionCenterInventoryHistory | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: DistributionCenterInventoryHistory) => {
        setAnchorEl(event.currentTarget);
        setSelectedHistoryRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedHistoryRow(null);
    };

    const handleView = () => {
        if (selectedHistoryRow) {
            void onShowInventoryHistory(selectedHistoryRow);
        }
        handleMenuClose();
    };

    const onShowInventoryHistory = async (inventoryHistory: DistributionCenterInventoryHistory) => {
        setSelectedHistory(inventoryHistory);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedHistory(null);
    };

    const columnsDetails = useMemo<GridColDef<any>[]>(
        () => [
            {
                field: 'name',
                flex: 3,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.input.name}</Typography>
            },
            {
                field: 'quantity',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Cantidad
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.quantity}</Typography>
            },
            {
                field: 'cost',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo unitario
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost}`}</Typography>
            },
            {
                field: 'total',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost * row.quantity}`}</Typography>
            }
        ],
        []
    );

    const getTotal = (inventoryHistory: DistributionCenterInventoryHistory) => {
        return inventoryHistory.inputs.reduce((a, item) => a + item.quantity * item.input.purchaseCost, 0).toFixed(2);
    };

    const getTotalQuantity = (inventoryHistory: DistributionCenterInventoryHistory) => {
        return inventoryHistory.inputs.reduce((a, item) => a + item.quantity, 0).toFixed(2);
    };

    const getData = async () => {
        showLoader();
        try {
            const inventoryHistoriesData = await distributionCenterInventoryHistoryService.getInventoryHistories(
                distributionCenterId
            );
            setInventoryHistories(inventoryHistoriesData);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, [distributionCenterId]);

    const columns = useMemo<ResponsiveGridColDef<DistributionCenterInventoryHistory>[]>(
        () => [
            {
                field: 'movementType',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Tipo de Movimiento
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Chip
                        label={row.movementType === 'input' ? 'Entrada' : 'Salida'}
                        color={row.movementType === 'input' ? 'success' : 'error'}
                        size="small"
                    />
                )
            },
            {
                field: 'date',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Fecha
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography>{row.date ? format(row.date.toDate(), 'dd-MM-yyyy HH:mm') : 'N/A'}</Typography>
                )
            },
            {
                field: 'total',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Monto Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${getTotal(row)}`}</Typography>
            },
            {
                field: 'quantity',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Cantidad Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`${getTotalQuantity(row)}`}</Typography>
            },
            {
                field: 'user',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Usuario
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.userData?.profile.name || 'Desconocido'}</Typography>
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} />
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar en historial"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={inventoryHistories}
                columns={columns}
                onCellDoubleClick={(cel) => onShowInventoryHistory(cel.row as DistributionCenterInventoryHistory)}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleView}>Ver</MenuItem>
            </Menu>

            <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            Detalle de Movimiento
                        </Typography>
                        {selectedHistory && (
                            <Chip
                                label={selectedHistory.movementType === 'input' ? 'Entrada' : 'Salida'}
                                color={selectedHistory.movementType === 'input' ? 'success' : 'error'}
                            />
                        )}
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedHistory && (
                        <Stack spacing={3}>
                            <Stack direction="row" alignItems="center" spacing={4}>
                                <Stack direction="column">
                                    <Typography>Monto Total</Typography>
                                    <Typography fontWeight={600}>{`$${getTotal(selectedHistory)}`}</Typography>
                                </Stack>
                                <Stack direction="column">
                                    <Typography>Fecha</Typography>
                                    <Typography fontWeight={600}>
                                        {selectedHistory.date
                                            ? format(selectedHistory.date.toDate(), 'dd-MM-yyyy HH:mm')
                                            : 'N/A'}
                                    </Typography>
                                </Stack>
                                <Stack direction="column">
                                    <Typography>Registrado por</Typography>
                                    <Typography fontWeight={600}>
                                        {`${selectedHistory.userData?.profile?.name || 'Desconocido'} (${
                                            selectedHistory.userData?.profile?.profile || 'N/A'
                                        })`}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Box height={300}>
                                <DataGrid
                                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                                    sx={{ borderWidth: 0 }}
                                    getRowId={(row) => `${selectedHistory.id}-${row.input.id}`}
                                    rows={selectedHistory.inputs || []}
                                    columns={columnsDetails}
                                />
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
export default ListInventoryHistory;
